import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {DocumentListComponent, NodesApiService, SitesService} from '@alfresco/adf-content-services';
import {Node, NodeEntry, SiteEntry} from '@alfresco/js-api';
import {finalize, from, timeout} from 'rxjs';

@Component({
  standalone: false,
  selector: 'alg-select-files',
  templateUrl: './select-files.component.html',
  styleUrls: ['./select-files.component.scss', './select-files-fixes.scss']
})
export class SelectFilesComponent implements OnInit {
  @Input() contextNode?: Node;
  @Input() selectedFiles: NodeEntry[] = [];
  @Output() output = new EventEmitter<NodeEntry[]>();
  @ViewChild('documentList') documentList?: DocumentListComponent;

  sites: SiteEntry[] = [];
  selectedSite?: SiteEntry;
  documentLibraryId?: string;
  loadingSites = true;
  loadingLibrary = false;
  sitesError = false;
  libraryError = false;
  private visibleFileIds = new Set<string>();
  readonly filesSelectableRowFilter = (row: {node?: NodeEntry; isSelectable: boolean}): boolean => {
    row.isSelectable = row.node?.entry?.isFile === true;
    return true;
  };

  constructor(private sitesService: SitesService, private nodesApiService: NodesApiService) {}

  ngOnInit(): void {
    this.loadSites();
  }

  loadSites(): void {
    this.loadingSites = true;
    this.sitesError = false;
    this.sitesService.getSites({maxItems: 100, orderBy: ['title ASC']}).pipe(
      timeout(15000),
      finalize(() => this.loadingSites = false)
    ).subscribe({
      next: (response) => {
        this.sites = response?.list?.entries || [];
        this.initializeFromContext();
      },
      error: () => {
        this.sites = [];
        this.sitesError = true;
      }
    });
  }

  selectLibrary(site: SiteEntry, initialFolderId?: string): void {
    this.selectedSite = site;
    this.documentLibraryId = undefined;
    this.loadingLibrary = true;
    this.libraryError = false;
    from(this.sitesService.sitesApi.getSiteContainer(site.entry.id, 'documentLibrary')).pipe(
      timeout(15000),
      finalize(() => this.loadingLibrary = false)
    ).subscribe({
      next: (container) => {
        this.documentLibraryId = initialFolderId || container.entry.id;
      },
      error: () => this.libraryError = true
    });
  }

  handleNodeOpen(event: Event): void {
    const node = (event as CustomEvent)?.detail?.node as NodeEntry;
    if (node?.entry?.isFolder) {
      this.visibleFileIds.clear();
      this.documentLibraryId = node.entry.id;
    }
  }

  onNodeSelected(nodes: NodeEntry[]): void {
    const files = (nodes || []).filter((node) => node?.entry?.isFile);
    const selectedIds = new Set(files.map((node) => node.entry.id));
    const merged = new Map(
      this.selectedFiles
        .filter((node) => !this.visibleFileIds.has(node.entry.id) || selectedIds.has(node.entry.id))
        .map((node) => [node.entry.id, node])
    );
    files.forEach((node) => merged.set(node.entry.id, node));
    this.selectedFiles = Array.from(merged.values());
    if (this.documentList) {
      this.documentList.selection = files;
      this.documentList.preselectNodes = this.selectedFiles;
    }
    this.emitSelection();
  }

  onFolderLoaded(paging: any): void {
    const entries: NodeEntry[] = paging?.list?.entries || [];
    this.visibleFileIds = new Set(entries.filter((node) => node.entry?.isFile).map((node) => node.entry.id));
    if (this.documentList) {
      this.documentList.selection = this.selectedFiles.filter((node) => this.visibleFileIds.has(node.entry.id));
      this.documentList.preselectNodes = this.selectedFiles;
    }
  }

  removeFile(file: NodeEntry): void {
    this.selectedFiles = this.selectedFiles.filter((node) => node.entry.id !== file.entry.id);
    if (this.documentList) {
      this.documentList.preselectNodes = this.selectedFiles;
      this.documentList.unselectRowFromNodeId(file.entry.id);
    }
    this.emitSelection();
  }

  viewerUrl(file: NodeEntry): string {
    return `#/libraries/${file.entry.parentId}/(viewer:view/${file.entry.id})`;
  }

  fileIcon(file: NodeEntry): string {
    const extension = file.entry.name?.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') return 'picture_as_pdf';
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension || '')) return 'image';
    return 'description';
  }

  fileSize(bytes?: number): string {
    if (!bytes) return 'Taille inconnue';
    const units = ['o', 'Ko', 'Mo', 'Go'];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / Math.pow(1024, index)).toLocaleString('fr-FR', {maximumFractionDigits: 1})} ${units[index]}`;
  }

  private initializeFromContext(): void {
    if (!this.contextNode?.id) return;
    if (!this.contextNode.path?.elements) {
      this.nodesApiService.getNode(this.contextNode.id, {include: ['path']}).subscribe({
        next: (node) => {
          this.contextNode = node;
          this.openContextLocation();
        }
      });
      return;
    }
    this.openContextLocation();
  }

  private openContextLocation(): void {
    const siteId = this.sitesService.getSiteNameFromNodePath(this.contextNode);
    const site = this.sites.find((entry) => entry.entry.id.toLowerCase() === siteId);
    if (!site) return;
    const folderId = this.contextNode.isFile ? this.contextNode.parentId : this.contextNode.id;
    this.selectLibrary(site, folderId);
  }

  private emitSelection(): void {
    this.output.emit([...this.selectedFiles]);
  }
}
